import React, { useCallback, useEffect, useState } from "react";
import { Box, Dialog, DialogContent, IconButton, Typography, alpha } from "@mui/material";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTranslation } from "react-i18next";
import { getMetaSocialFromMessage, storyExpiredPlaceholder } from "../../utils/metaMessageSocial";
import { downloadChatMedia, filenameFromMediaUrl } from "../../utils/downloadChatMedia";

/** Resolve relative chat media paths to an absolute URL. */
export function resolveChatImageUrl(raw) {
  if (typeof raw !== "string") return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("blob:") ||
    trimmed.startsWith("data:")
  ) {
    return trimmed;
  }
  return `https://xvision.xpro.me${trimmed}`;
}

/** Chronological image URLs from a conversation message list. */
export function buildConversationImageGallery(messages) {
  if (!Array.isArray(messages)) return [];
  const urls = [];
  for (const m of messages) {
    const url = resolveChatImageUrl(m?.image || "");
    if (url) urls.push(url);
  }
  return urls;
}

/** Index of this message among image-bearing messages (by id, then URL). */
export function getConversationImageGalleryIndex(messages, message) {
  const gallery = buildConversationImageGallery(messages);
  if (!gallery.length || !message) return 0;

  if (Array.isArray(messages) && message.id != null) {
    let idx = 0;
    for (const m of messages) {
      const url = resolveChatImageUrl(m?.image || "");
      if (!url) continue;
      if (m?.id != null && String(m.id) === String(message.id)) return idx;
      idx += 1;
    }
  }

  const current = resolveChatImageUrl(message?.image || "");
  const fallback = gallery.indexOf(current);
  return fallback >= 0 ? fallback : 0;
}

const navButtonSx = {
  position: "fixed",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1,
  color: "common.white",
  bgcolor: alpha("#000", 0.45),
  "&:hover": { bgcolor: alpha("#000", 0.65) },
};

const ImageMessage = ({ message, galleryUrls, galleryIndex = 0 }) => {
  const { t } = useTranslation("chat");
  const [imageError, setImageError] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const imageUrl = resolveChatImageUrl(message?.image || "");
  const gallery =
    Array.isArray(galleryUrls) && galleryUrls.length > 0
      ? galleryUrls
      : imageUrl
        ? [imageUrl]
        : [];
  const canNavigate = gallery.length > 1;
  const previewUrl = open ? gallery[activeIndex] || imageUrl : imageUrl;
  const metaSocial = getMetaSocialFromMessage(message);
  const expiredText = storyExpiredPlaceholder(metaSocial);

  // Blob local pode falhar/revogar; quando a URL permanente chega, limpa o estado de erro.
  useEffect(() => {
    setImageError(false);
    setPreviewError(false);
  }, [imageUrl]);

  const openPreview = () => {
    const start =
      typeof galleryIndex === "number" && galleryIndex >= 0 && galleryIndex < gallery.length
        ? galleryIndex
        : Math.max(0, gallery.indexOf(imageUrl));
    setActiveIndex(start);
    setPreviewError(false);
    setOpen(true);
  };

  const closePreview = () => setOpen(false);

  const goPrev = useCallback(() => {
    if (gallery.length < 2) return;
    setActiveIndex((i) => (i - 1 + gallery.length) % gallery.length);
    setPreviewError(false);
  }, [gallery.length]);

  const goNext = useCallback(() => {
    if (gallery.length < 2) return;
    setActiveIndex((i) => (i + 1) % gallery.length);
    setPreviewError(false);
  }, [gallery.length]);

  useEffect(() => {
    if (!open || !canNavigate) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, canNavigate, goPrev, goNext]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          gap: 0.5,
        }}
      >
        {imageError || !imageUrl ? (
          <>
            <ImageNotSupportedIcon sx={{ fontSize: 60, color: "gray" }} />
            {expiredText ? (
              <Typography variant="caption" color="text.secondary" align="center" sx={{ px: 1 }}>
                {expiredText}
              </Typography>
            ) : null}
          </>
        ) : (
          <Box
            component="img"
            src={imageUrl}
            alt="Imagem enviada"
            sx={{
              maxWidth: 220,
              maxHeight: 220,
              borderRadius: 1,
              cursor: "pointer",
              objectFit: "cover",
            }}
            onClick={openPreview}
            onError={() => setImageError(true)}
          />
        )}
      </Box>

      <Dialog
        open={open}
        onClose={closePreview}
        maxWidth={false}
        aria-label="Visualização da imagem"
        PaperProps={{
          sx: {
            bgcolor: "transparent",
            boxShadow: "none",
            overflow: "visible",
            maxWidth: "calc(100dvw - 24px)",
            maxHeight: "calc(100dvh - 24px)",
            m: 1.5,
          },
        }}
        slotProps={{
          backdrop: {
            sx: { bgcolor: alpha("#000", 0.88) },
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: "relative",
            overflow: "visible",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "transparent",
          }}
        >
          <IconButton
            onClick={() =>
              downloadChatMedia(previewUrl, filenameFromMediaUrl(previewUrl, "imagem.jpg"))
            }
            aria-label={t("webchat.downloadAria")}
            sx={{
              position: "fixed",
              top: 12,
              right: 60,
              zIndex: 1,
              color: "common.white",
              bgcolor: alpha("#000", 0.45),
              "&:hover": { bgcolor: alpha("#000", 0.65) },
            }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={closePreview}
            aria-label="Fechar imagem"
            sx={{
              position: "fixed",
              top: 12,
              right: 12,
              zIndex: 1,
              color: "common.white",
              bgcolor: alpha("#000", 0.45),
              "&:hover": { bgcolor: alpha("#000", 0.65) },
            }}
          >
            <CloseIcon />
          </IconButton>

          {canNavigate ? (
            <>
              <IconButton
                onClick={goPrev}
                aria-label="Imagem anterior"
                sx={{ ...navButtonSx, left: { xs: 4, sm: 12 } }}
              >
                <NavigateBeforeIcon fontSize="large" />
              </IconButton>
              <IconButton
                onClick={goNext}
                aria-label="Próxima imagem"
                sx={{ ...navButtonSx, right: { xs: 4, sm: 12 } }}
              >
                <NavigateNextIcon fontSize="large" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  position: "fixed",
                  bottom: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 1,
                  color: "common.white",
                  bgcolor: alpha("#000", 0.45),
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  userSelect: "none",
                }}
              >
                {activeIndex + 1} / {gallery.length}
              </Typography>
            </>
          ) : null}

          {!previewError && previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Imagem enviada"
              sx={{
                maxWidth: "calc(100dvw - 24px)",
                maxHeight: "calc(100dvh - 24px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                display: "block",
                userSelect: "none",
              }}
              onError={() => setPreviewError(true)}
            />
          ) : (
            <ImageNotSupportedIcon sx={{ fontSize: 80, color: "grey.500" }} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageMessage;
